import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  PhoneMissed,
  MessageSquare,
  CalendarX2,
  BellOff,
  Globe,
  Tags,
  Users,
  CalendarClock,
  UserRound,
  BellRing,
  Scissors,
  Hand,
  Brush,
  Home,
  ArrowRight,
} from 'lucide-react';
import Contact from '@/src/components/Contact';
import About from '@/src/components/About';
import LeadForm from '@/src/components/LeadForm';

// Problems salon owners face today.
const PROBLEMS = [
  {
    icon: PhoneMissed,
    title: 'Missed calls during appointments',
    body: "You're with a client and the phone keeps ringing. Every missed call is a lost booking.",
  },
  {
    icon: MessageSquare,
    title: 'Too many WhatsApp messages',
    body: 'Juggling back-and-forth chats to agree on a time eats hours out of every week.',
  },
  {
    icon: CalendarX2,
    title: 'Manual scheduling mistakes',
    body: 'Double bookings and pen-and-paper slip-ups frustrate clients and cost you money.',
  },
  {
    icon: BellOff,
    title: 'Forgotten appointments',
    body: 'No-shows leave gaps in your day that could have been filled by paying clients.',
  },
  {
    icon: Globe,
    title: 'No simple way to book online',
    body: 'Clients expect to book in a few taps. Without it, they go to a competitor who lets them.',
  },
];

// Core platform features.
const FEATURES = [
  {
    icon: Globe,
    title: 'Online booking page',
    body: 'A clean, shareable page where clients book 24/7, no calls, no waiting.',
  },
  {
    icon: Tags,
    title: 'Service & price management',
    body: 'Set up your services, durations, and prices in minutes and update them anytime.',
  },
  {
    icon: Users,
    title: 'Staff availability',
    body: 'Manage working hours and availability for each team member with ease.',
  },
  {
    icon: CalendarClock,
    title: 'Appointment calendar',
    body: 'See your whole day, week, or month at a glance in one organized calendar.',
  },
  {
    icon: UserRound,
    title: 'Customer management',
    body: 'Keep client details and booking history in one place to deliver personal service.',
  },
  {
    icon: BellRing,
    title: 'Email & WhatsApp reminders',
    body: 'Automatic reminders cut no-shows and keep your schedule full.',
  },
];

// Audience segments.
const AUDIENCE = [
  { icon: Sparkles, label: 'Beauty salons' },
  { icon: Hand, label: 'Nail artists' },
  { icon: Scissors, label: 'Hairdressers' },
  { icon: Scissors, label: 'Barbers' },
  { icon: Brush, label: 'Estheticians' },
  { icon: Home, label: 'At-home beauty pros' },
];

export default function ShopDirectory() {
  const [leadFormOpen, setLeadFormOpen] = useState(false);

  // Allow the header "Start free" button (and any other page) to open the
  // single shared lead form via a lightweight custom event.
  useEffect(() => {
    const openLead = () => setLeadFormOpen(true);
    window.addEventListener('calendyz:open-lead', openLead);
    return () => window.removeEventListener('calendyz:open-lead', openLead);
  }, []);

  return (
    <div className="bg-surface min-h-screen">
      {/* ── Hero (B2B) ───────────────────────────────────────────── */}
      <div className="from-accent-soft via-accent-soft/55 to-surface relative overflow-hidden bg-gradient-to-b">
        {/* Soft baby-blue atmosphere. The light-mode tints are far too bright
            against a dark canvas, so dark gets its own dimmer pair. */}
        <div
          aria-hidden
          className="bg-brand-200/55 dark:bg-brand-600/20 pointer-events-none absolute -top-28 -left-20 h-80 w-80 rounded-full blur-3xl"
        />
        <div
          aria-hidden
          className="bg-brand-100/70 dark:bg-brand-500/15 pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-28 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="border-accent/30 text-accent bg-surface/70 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5" />
              Booking software for beauty professionals
            </span>
            <h1 className="text-fg mt-5 text-4xl leading-[1.05] font-medium tracking-tight sm:text-6xl">
              Grow your salon with <span className="text-accent italic">online booking</span>
            </h1>
            <p className="text-fg-muted mx-auto mt-5 max-w-xl text-base sm:text-lg">
              Calendyz helps beauty professionals accept bookings 24/7, manage appointments, and
              reduce missed messages, all from one simple platform.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setLeadFormOpen(true)}
                className="bg-brand-600 hover:bg-brand-700 shadow-brand-600/20 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-medium text-white shadow-lg transition-colors sm:w-auto"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setLeadFormOpen(true)}
                className="border-default bg-surface text-fg-secondary hover:bg-canvas inline-flex w-full items-center justify-center rounded-xl border px-6 py-3.5 text-sm font-medium shadow-sm transition-colors sm:w-auto"
              >
                Request a demo
              </button>
            </div>
            <p className="text-fg-subtle mt-4 text-xs">
              Free during beta · No credit card required
            </p>
          </div>
        </div>
      </div>

      {/* ── Problems ─────────────────────────────────────────────── */}
      <section id="problems" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-accent text-base font-semibold tracking-wide uppercase">
            The daily struggle
          </h2>
          <p className="font-display text-fg mt-2 text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
            Running a salon shouldn't mean chasing messages
          </p>
          <p className="text-fg-muted mt-4 text-lg">
            If any of these sound familiar, you're losing time and bookings every week.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROBLEMS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="border-subtle bg-surface rounded-2xl border p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="text-accent bg-accent-soft flex h-11 w-11 items-center justify-center rounded-xl">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-fg mt-4 font-medium">{title}</h3>
              <p className="text-fg-muted mt-1.5 text-sm">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features / Solution ──────────────────────────────────── */}
      <section id="features" className="border-subtle bg-canvas border-y py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-accent text-base font-semibold tracking-wide uppercase">
              Everything in one place
            </h2>
            <p className="font-display text-fg mt-2 text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
              One simple platform to run your bookings
            </p>
            <p className="text-fg-muted mt-4 text-lg">
              Replace the calls, notebooks, and endless chats with tools built for beauty
              professionals.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="border-subtle bg-surface rounded-2xl border p-6 shadow-sm transition-shadow hover:shadow-lg"
              >
                <div className="bg-brand-600 flex h-12 w-12 items-center justify-center rounded-xl text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-fg mt-4 text-lg font-medium">{title}</h3>
                <p className="text-fg-muted mt-1.5 text-sm">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who is it for ────────────────────────────────────────── */}
      <section id="for-salons" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-accent text-base font-semibold tracking-wide uppercase">
            Who is it for
          </h2>
          <p className="font-display text-fg mt-2 text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
            Built for every kind of beauty business
          </p>
          <p className="text-fg-muted mt-4 text-lg">
            Whether you run a busy salon or work on your own, Calendyz fits the way you work.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {AUDIENCE.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="border-subtle bg-surface flex flex-col items-center gap-3 rounded-2xl border p-6 text-center shadow-sm"
            >
              <div className="bg-accent-soft text-accent flex h-12 w-12 items-center justify-center rounded-full">
                <Icon className="h-6 w-6" />
              </div>
              <span className="text-fg text-sm font-medium">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing teaser ───────────────────────────────────────── */}
      <section id="pricing" className="bg-canvas py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* The brand ramp brightens under `.dark` (index.css) so the opaque band
              tops out at 3.79:1 for white text there. Fading the stops to 60%
              composites them down onto `bg-canvas` — same hue, AA-legible. */}
          <div className="from-brand-600 to-brand-700 dark:from-brand-600/60 dark:to-brand-700/60 relative overflow-hidden rounded-3xl bg-gradient-to-br px-6 py-14 text-center shadow-xl sm:px-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-20 -right-16 h-72 w-72 rounded-full bg-white/10 blur-3xl"
            />
            <div className="relative mx-auto max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" />
                Beta access open
              </span>
              <h2 className="font-display mt-5 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Simple pricing for growing salons
              </h2>
              <p className="mt-4 text-lg text-white/95">
                Start with Calendyz for free during the beta. Paid plans will be simple and
                affordable for small beauty businesses.
              </p>
              <button
                type="button"
                onClick={() => setLeadFormOpen(true)}
                className="text-accent hover:bg-accent-soft bg-surface mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-medium shadow-lg transition-colors"
              >
                Join the beta
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="about">
        <About />
      </section>

      <section id="contact">
        <Contact />
      </section>

      <LeadForm open={leadFormOpen} onClose={() => setLeadFormOpen(false)} />
    </div>
  );
}
